import { Link, NavLink, useParams, Navigate } from "react-router-dom";
import { Stamp, SectionLabel, Headline, HandCheck } from "@/components/Doc";
import { MODULES, moduleBySlug } from "@/data/modules";

const pad = (n) => String(n).padStart(2, "0");

export default function ModuleDetail() {
  const { slug } = useParams();
  const m = moduleBySlug(slug);
  if (!m) return <Navigate to="/modules" replace />;

  return (
    <main className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20" data-testid={`module-page-${m.slug}`}>
      <nav className="flex gap-1 overflow-x-auto border-b border-carbon" data-testid="folder-tabs">
        {MODULES.map((t) => (
          <NavLink key={t.slug} to={`/modules/${t.slug}`} className={({ isActive }) => `folder-tab ${isActive ? "active" : ""}`} data-testid={`tab-${t.slug}`}>
            {t.numeral} — {t.title}
          </NavLink>
        ))}
      </nav>

      <header className="relative pt-14 pb-16 border-b border-rule">
        <div className="absolute top-12 right-0"><Stamp color="red" rotate={6} size="md">For internal use</Stamp></div>
        <span className="t-label text-olive">Module {m.numeral}</span>
        <Headline as="h1" className="mt-3 text-5xl sm:text-6xl lg:text-8xl">{m.title}</Headline>
        <p className="t-body text-xl sm:text-2xl mt-6 max-w-[50ch]" style={{ color: m.accent }}>{m.description}</p>
        <p className="t-label mt-8 text-carbon" data-testid="module-meta">{m.meta}</p>
      </header>

      <section className="py-16 grid lg:grid-cols-12 gap-10">
        <SectionLabel className="lg:col-span-3 lg:block lg:after:hidden">Doc. {m.numeral} — 01 — The Problem</SectionLabel>
        <div className="lg:col-span-8 lg:col-start-5 t-body text-lg sm:text-xl leading-[1.65] flex flex-col gap-6">
          {m.problemLong.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </section>

      <section className="py-16 border-t border-rule" data-testid="drills-section">
        <SectionLabel>Doc. {m.numeral} — 02 — The Drills</SectionLabel>
        <div className="mt-10 flex flex-col">
          {m.drills.map((d, i) => (
            <div key={d.title} className="grid sm:grid-cols-12 gap-4 py-8 border-b border-rule last:border-0" data-testid={`drill-${i + 1}`}>
              <span className="t-label sm:col-span-2">Drill {pad(i + 1)}</span>
              <div className="sm:col-span-10">
                <h3 className="t-head text-3xl sm:text-4xl">{d.title}</h3>
                <p className="t-body text-lg sm:text-xl mt-4 max-w-[65ch] leading-[1.65]">{d.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 border-t border-rule" data-testid="keep-section">
        <SectionLabel>Doc. {m.numeral} — 03 — What You Keep</SectionLabel>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {m.keep.map((k, i) => (
            <div key={k.name} className="relative bg-paper border border-carbon p-6 pt-14 shadow-[4px_4px_0_0_#D9C9A8]">
              <div className="absolute top-4 right-4"><Stamp color="carbon" rotate={i % 2 ? 5 : -5} size="sm">{k.stamp}</Stamp></div>
              <span className="t-label text-rule">Exhibit {pad(i + 1)}</span>
              <h3 className="t-head text-2xl mt-2 leading-none">{k.name}</h3>
              <p className="t-body text-base mt-3 text-olive">{k.caption}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 border-t border-rule grid lg:grid-cols-12 gap-10" data-testid="changes-section">
        <div className="lg:col-span-4">
          <SectionLabel className="lg:after:hidden">Doc. {m.numeral} — 04 — What Changes</SectionLabel>
          <p className="t-body text-base text-olive mt-4 max-w-xs">Written into the closing memorandum, each with an owner and a date.</p>
        </div>
        <ul className="lg:col-span-8 flex flex-col gap-5">
          {m.changes.map((c) => (
            <li key={c} className="flex gap-4 t-body text-lg sm:text-xl leading-snug"><HandCheck />{c}</li>
          ))}
        </ul>
      </section>

      <section className="py-16 border-t border-rule grid lg:grid-cols-12 gap-10" data-testid="room-section">
        <SectionLabel className="lg:col-span-4 lg:after:hidden">Doc. {m.numeral} — 05 — In the Room</SectionLabel>
        <p className="lg:col-span-8 t-body text-lg sm:text-xl leading-[1.65]">{m.room}</p>
      </section>

      <section className="py-16 border-t border-carbon flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <span className="t-label text-olive">Form LH-{pad(20 + MODULES.indexOf(m))}</span>
          <h2 className="t-head text-3xl sm:text-4xl mt-2">Module {m.numeral} for your team.</h2>
        </div>
        <Link to={`/contact?interest=module-${m.slug}`} className="btn btn-stamp" data-testid="enquire-module-btn">Enquire About This Module</Link>
      </section>
    </main>
  );
}
